import { API_BASE, apiFetch } from "../../api/http";

export interface InjectionFinding {
  quote: string;
  reason: string;
  start: number;
}

/** Ask the backend whether the document contains text written to steer an AI review. */
export async function postInjectionScan(
  docContent: string,
  timeoutMs = 30000
): Promise<{ findings: InjectionFinding[]; partial: boolean }> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      reject(new Error("injectionScan timed out"));
      controller.abort();
    }, timeoutMs);
  });
  try {
    // Bound both the HTTP request and body read. A stalled optional check must
    // release the caller to its existing 'scan unavailable' analysis path.
    return await Promise.race([deadline, (async () => {
      const res = await apiFetch(`${API_BASE}/api/security/injectionScan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ docContent }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`injectionScan failed: ${res.status}`);
      const result = await res.json();
      if (!result || !Array.isArray(result.findings) || typeof result.partial !== "boolean" ||
          result.findings.some((finding: InjectionFinding) => !finding ||
            typeof finding.quote !== "string" || !finding.quote.trim() ||
            typeof finding.reason !== "string" || !Number.isInteger(finding.start) || finding.start < 0 ||
            docContent.slice(finding.start, finding.start + finding.quote.length) !== finding.quote)) {
        throw new Error("injectionScan returned an invalid response");
      }
      return result;
    })()]);
  } finally {
    clearTimeout(timer!);
  }
}
